// src/pages/ClassSummary.jsx
// 授業ごとのまとめ（出席集計・回ごとサマリ・授業メモ）【2026-07-22】
import React, { useEffect, useState } from 'react';
import PeriodBar, { usePeriod } from '../components/PeriodBar';

const token = () => localStorage.getItem('token');
const auth  = () => ({ Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' });

const STATUS_LABEL = { active: '進行中', completed: '終了', cancelled: '休講' };

function fmtDate(d) {
  if (!d) return '-';
  return String(d).slice(0, 10);
}

export default function ClassSummary() {
  const [allClasses, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const period = usePeriod();   // 今の期間の授業だけ【2026-09-17】
  const classes = period.filter(allClasses);
  const [data, setData]       = useState(null);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const [msg, setMsg]         = useState('');

  // メモ編集状態
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft]         = useState('');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!period.ready) return;
    fetch('/api/schedule/classes?includeClosed=1' + (period.year ? '&academicYear=' + period.year : ''), { headers: auth() })
      .then(r => r.json())
      .then(d => {
        const rows = d.classes || d.data || [];
        // class_schedules との JOIN で同じ授業が複数行になるため id で重複排除
        const seen = new Map();
        rows.forEach(c => { if (!seen.has(c.id)) seen.set(c.id, c); });
        setClasses([...seen.values()]);
      })
      .catch(() => {});
  }, [period.ready, period.year]);

  const load = async (id) => {
    if (!id) { setData(null); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await fetch('/api/class-summary/' + id, { headers: auth() });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
      setData(d);
    } catch (e) { setErr(e.message); setData(null); }
    finally     { setBusy(false); }
  };

  const onSelect = (e) => {
    const id = e.target.value;
    setClassId(id);
    setEditingId(null);
    load(id);
  };

  const startEdit = (session) => {
    setEditingId(session.id);
    setDraft(session.lesson_notes || '');
    setMsg('');
  };

  const saveNotes = async (sessionId) => {
    setSaving(true); setErr('');
    try {
      const res = await fetch('/api/class-summary/sessions/' + sessionId + '/notes', {
        method: 'PUT',
        headers: auth(),
        body: JSON.stringify({ notes: draft }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
      setMsg('メモを保存しました ');
      setEditingId(null);
      await load(classId);
    } catch (e) { setErr(e.message); }
    finally     { setSaving(false); }
  };

  const ov = data && data.overview;

  return (
    <div className="page-container page-wide">
      <div className="page-header">
        <h1>出席記録</h1>
        <p>授業ごとの出席集計・回ごとのサマリ・授業メモを確認できます。</p>
      </div>

      <div className="card">
        <div className="card-header"><h2>授業を選択</h2></div>
        <div className="form-row">
          <label>授業 *</label>
          <PeriodBar period={period} hiddenCount={allClasses.length - classes.length} />
          <select value={classId} onChange={onSelect}>
            <option value="">-- 選択してください --</option>
            {classes.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.semester ? '（' + c.semester + '）' : ''}</option>
            ))}
          </select>
        </div>
        {busy && <p>読み込み中...</p>}
        {err  && <p style={{ color: '#dc2626' }}>{err}</p>}
        {msg  && <p style={{ color: '#16a34a' }}>{msg}</p>}
      </div>

      {ov && (
        <div className="card">
          <div className="card-header"><h2>全体集計</h2></div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">履修者数</div>
              <div className="stat-value">{ov.enrolled_students}<span className="stat-unit">人</span></div>
            </div>
            <div className="stat-card">
              <div className="stat-label">授業回数</div>
              <div className="stat-value">{ov.total_sessions}<span className="stat-unit">回</span></div>
            </div>
            <div className="stat-card">
              <div className="stat-label">休講</div>
              <div className="stat-value">{ov.cancelled_sessions}<span className="stat-unit">回</span></div>
            </div>
            <div className="stat-card">
              <div className="stat-label">平均出席率</div>
              <div className="stat-value">{ov.average_attendance_rate != null ? ov.average_attendance_rate : '-'}<span className="stat-unit">%</span></div>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="card">
          <div className="card-header"><h2>回ごとのサマリ・授業メモ</h2></div>
          {data.sessions.length === 0 && <p>まだ授業セッションがありません。</p>}
          {data.sessions.map(s => (
            <div key={s.id} style={{ borderBottom: '1px solid #e5e7eb', padding: '0.75rem 0' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'baseline' }}>
                <strong>{fmtDate(s.session_date)}</strong>
                <span>{STATUS_LABEL[s.status] || s.status}</span>
                <span>出席 {s.attended_count} / {ov.enrolled_students} 人</span>
                <span style={{ color: '#6b7280' }}>
                  （出席 {s.present_count} / 遅刻 {s.late_count} / 早退 {s.early_leave_count}）
                </span>
                {editingId !== s.id && (
                  <button className="btn-secondary" onClick={() => startEdit(s)}>
                    {s.lesson_notes ? 'メモを編集' : 'メモを書く'}
                  </button>
                )}
              </div>

              {editingId === s.id ? (
                <div style={{ marginTop: '0.5rem' }}>
                  <textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    rows={4}
                    maxLength={10000}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                    placeholder="授業内容・連絡事項などを記録"
                  />
                  <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem' }}>
                    <button className="btn-primary" disabled={saving} onClick={() => saveNotes(s.id)}>
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button className="btn-secondary" disabled={saving} onClick={() => setEditingId(null)}>
                      キャンセル
                    </button>
                  </div>
                </div>
              ) : (
                s.lesson_notes && (
                  <div style={{ marginTop: '0.4rem', whiteSpace: 'pre-wrap', background: '#f9fafb', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                    {s.lesson_notes}
                    {s.lesson_notes_updated_at && (
                      <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        更新: {String(s.lesson_notes_updated_at).slice(0, 16).replace('T', ' ')}
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {data && data.students.length > 0 && (
        <div className="card">
          <div className="card-header"><h2>学生別の出席状況</h2></div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>学籍番号</th>
                  <th>氏名</th>
                  <th>出席</th>
                  <th>遅刻</th>
                  <th>早退</th>
                  <th>出席率</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map(st => (
                  <tr key={st.id}>
                    <td>{st.student_id}</td>
                    <td>{st.name}</td>
                    <td>{st.present_count}</td>
                    <td>{st.late_count}</td>
                    <td>{st.early_leave_count}</td>
                    <td>{st.attendance_rate != null ? st.attendance_rate + '%' : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
